/**
 * What a MultiPro (firmware V0.77+) reads on its own RS485 port, set up with the Toolbox like a
 * Milesight UC300: the line, and each channel with the reading its values are filed as. A
 * channel whose register isn't a Modbus port in the app yet still files its values (as
 * "mb<slave>_<function>_<register>"), but with no name, unit or scale: the card says so.
 * Read now asks the board to read straight away instead of waiting for its interval.
 */
import { useState } from "react";
import { api, ApiError, type BoardModbusChannel, type CommandRecord, type Device } from "../api.ts";
import { FINAL } from "../control.ts";
import { ago } from "../format.ts";
import { Section, useLoad } from "../ui.tsx";
import { Lifecycle, useCommandWatch, useListenOnly } from "./control.tsx";

const message = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong.");
const FC = { 1: "coil", 2: "discrete input", 3: "holding register", 4: "input register" } as Record<number, string>;
const TYPE: Record<string, string> = {
  uint16: "16-bit", int16: "16-bit signed", uint32: "32-bit", int32: "32-bit signed", float: "float",
  uint32_ws: "32-bit, low word first", int32_ws: "32-bit signed, low word first", float_ws: "float, low word first", bit: "on/off",
};

export function BoardModbusCard({ device, now }: { device: Device; now: number }) {
  const data = useLoad(() => api.boardModbus(device.devEui).catch(() => null), [device.devEui], 60_000);
  const listenOnly = useListenOnly();
  const { cmds, track } = useCommandWatch(device.devEui, data.reload);
  const [error, setError] = useState<string | null>(null);
  const d = data.data;
  if (device.model !== "multipro" || !d || !d.supported) return null;
  // Nothing set up and never reported: keep the page quiet.
  if (!d.channels.length && !d.serial) return null;
  const blocked = listenOnly || !d.drives;
  const busy = (k: string) => cmds[k] !== undefined && !FINAL.has(cmds[k]!.status);
  const go = async (k: string, fn: () => Promise<{ command: CommandRecord }>) => {
    setError(null);
    try {
      track(k, (await fn()).command);
    } catch (e) {
      setError(message(e));
    }
  };
  const s = d.serial;
  return (
    <Section
      title="Modbus device on the board"
      right={
        <button className="btn ghost" disabled={blocked || busy("all")} onClick={() => go("all", () => api.readBoardModbus(device.devEui, "all"))}>
          Read now
        </button>
      }
    >
      <div className="muted small" style={{ marginBottom: 8 }}>
        The board reads these itself{s ? ` every ${s.intervalSeconds} s (${s.baud} 8${s.parity[0]!.toUpperCase()}${s.stopBits})` : ""} and sends them with its reports. They're set up with the Toolbox.
        {d.at ? ` Last told ${ago(d.at, now)}.` : ""}
      </div>
      {blocked && <div className="banner warn small">Read now is off for now: Think-Control is only listening while FMS-Control still runs this farm.</div>}
      {d.channels.length === 0 ? (
        <div className="card empty">
          No channels set up on the board.{" "}
          <button className="btn ghost" disabled={blocked || busy("ask")} onClick={() => go("ask", () => api.refreshBoardModbus(device.devEui))}>
            Ask the board again
          </button>
        </div>
      ) : (
        <div className="card list">
          {d.channels.map((c) => (
            <Row key={c.channel} c={c} />
          ))}
        </div>
      )}
      {cmds.all && <Lifecycle c={cmds.all} />}
      {cmds.ask && <Lifecycle c={cmds.ask} />}
      {error && <div className="banner crit small">{error}</div>}
    </Section>
  );
}

function Row({ c }: { c: BoardModbusChannel }) {
  const where = `slave ${c.slave}, ${FC[c.fc] ?? `function ${c.fc}`} ${c.address}${c.type === "bit" && c.fc >= 3 ? ` bit ${c.bit}` : ""}, ${TYPE[c.type] ?? c.type}`;
  return (
    <div className="ctl">
      <div style={{ minWidth: 0, flex: "1 1 260px" }}>
        <div className="t">
          {c.channel}. {c.name ?? <span className="muted">Not named</span>}
          {c.unit ? <span className="muted small"> ({c.unit})</span> : null}
          {c.enabled === false && <span className="pill warn">Port off</span>}
        </div>
        <div className="d">
          {where}
          {c.name === null && " · not a port in the app yet, so its values have no name, unit or scale: add the register as a Modbus port in Set up."}
        </div>
      </div>
    </div>
  );
}
